import React, { useEffect, useMemo } from 'react';
import { useSuccessBar } from '../context/SuccessBarContext';
import Tooltip from '../tooltip/Tooltip';
import { copyText } from '../../util/clipboard';
import useAddOns from '../../hooks/useAddOns';
import { ADD_ON_STATIC_PLACEHOLDERS, placeholderTokenEntries } from '../../util/addOns';

// Alle Platzhalter verwenden geschweifte Klammern {}.
// Klick kopiert den Platzhalter in die Zwischenablage.
// slotOnly: only offered when the textblock is used for a mail type that provides it
// (GET /Textblock/Slots → placeholders), never in the general list.
const PLACEHOLDERS_BEFORE_ADD_ONS = [
  { key: '{Vorname}',      label: 'Vorname des Spielers' },
  { key: '{Nachname}',     label: 'Nachname des Spielers' },
  { key: '{Name}',         label: 'Vor- und Nachname' },
  { key: '{Email}',        label: 'E-Mail-Adresse des Spielers' },
  { key: '{Datum}',        label: 'Spieltag-Datum (TT.MM.JJJJ)' },
  { key: '{Spieltag}',     label: 'Spieltag-Datum (TT.MM.JJJJ), gleichbedeutend mit {Datum}', slotOnly: true },
  { key: '{Von}',          label: 'Startzeit des Spieltags (HH:MM)' },
  { key: '{Bis}',          label: 'Endzeit des Spieltags (HH:MM)' },
  { key: '{PreisEintritt}',label: 'Eintrittspreis für Gäste am Spieltag; ohne Spieltag-Bezug der Gast-Standardpreis (z. B. 20,00 €)' },
  { key: '{Gruppe}',              label: 'Name der Gruppe des Empfängers (z. B. „Mitglied“) — bei Willkommens- und Prozess-Mails die Gruppe, für die die Mail gilt' },
  { key: '{PreisEintrittGruppe}', label: 'Eintrittspreis der Gruppe des Empfängers am Spieltag; ohne Spieltag-Bezug ihr Standardpreis (z. B. 0,00 €)' },
];

// Add-on placeholders ({PreisK}, {PreisKGruppe}, {KHinweis} per catalog key) follow here — generated from the
// catalog (useAddOns), not listed statically.

const PLACEHOLDERS_AFTER_ADD_ONS = [
  { key: '{Betrag}',       label: 'Zu zahlender Betrag des Spielers für diesen Spieltag inkl. Anmelde-Zusätze (z. B. 30,00 €) — nur Mails mit Spieltag-Bezug' },
  ...ADD_ON_STATIC_PLACEHOLDERS,
  { key: '{Ort}',          label: 'Name der Location des Spieltags — nur Mails mit Spieltag-Bezug' },
  { key: '{OrtAdresse}',   label: 'Adresse der Location (leer, wenn keine hinterlegt) — nur Mails mit Spieltag-Bezug' },
  { key: '{OrtKartenlink}',label: 'Google-Maps-Link zur Location — nur Mails mit Spieltag-Bezug' },
  { key: '{KontaktdatenOrga}', label: 'Orga-Kontaktdaten: Vorname + Telefon, kommagetrennt' },
  { key: '{TageVorEvent}',     label: 'Abstand bis zum Termin, z. B. „1 Tag“ oder „2 Tagen“ — nur Erinnerung vor dem Termin', slotOnly: true },
  { key: '{ResponseLink}',     label: 'Link zur Antwortseite (Platz annehmen oder ablehnen) — nur „Platz frei geworden“', slotOnly: true },
  { key: '{NewsletterSubscribeLink}',   label: 'Newsletter-Anmeldelink für den Mail-Empfänger — Single-Use-Token, 30 Tage gültig' },
  { key: '{NewsletterUnsubscribeLink}', label: 'Newsletter-Abmeldelink — nur in Newsletter-Mails sinnvoll; wird pro Empfänger ersetzt' },
  { key: '{AusgewaehlteSpieltage}',     label: 'Liste der gewählten Spieltage (nur in Ankündigungen); bei nicht öffentlichen Events zusätzlich Ort und z. B. „für Mitglieder kostenlos“ bzw. „auch für Mitglieder kostenpflichtig“ (Gruppe der Mail)' },
  { key: '{PersoenlicherLink}',         label: 'Persönlicher Link des Empfängers zu seiner Spieltagsseite — Single-Use-Token. Nur für Gruppen mit Anmeldeweg „Persönlicher Link“ (z. B. Mitglied); bei anderen Gruppen wird die Mail nicht versendet' },
];

// Generated placeholders of every catalog add-on with a placeholder key, in catalog order (by name).
function addOnPlaceholders(addOns) {
  return addOns
    .filter(a => a.placeholderKey)
    .flatMap(a => placeholderTokenEntries(a.placeholderKey, a.name));
}

// keys: placeholders of the mail type(s) the textblock is used for (null = general list). Add-on placeholders in
// the slot list come from the slot endpoint (one set per catalog key); their labels come from the catalog.
// Known keys keep the order above; keys this list does not describe (yet) are appended.
function visiblePlaceholders(keys, addOnEntries) {
  const all = [...PLACEHOLDERS_BEFORE_ADD_ONS, ...addOnEntries, ...PLACEHOLDERS_AFTER_ADD_ONS];
  if (!Array.isArray(keys)) return all.filter(p => !p.slotOnly);
  const wanted = new Set(keys);
  const described = new Set(all.map(p => p.key));
  const known = all.filter(p => wanted.has(p.key));
  const unknown = keys
    .filter((k, i) => !described.has(k) && keys.indexOf(k) === i)
    .map(k => ({ key: k, label: 'Platzhalter dieser Mail-Art' }));
  return [...known, ...unknown];
}

function TextblockEditorPlaceholder({ keys = null }) {
  const { startSuccessBarSuccess } = useSuccessBar();
  // Shared, offline-cached catalog (one request per page load, whatever number of placeholder lists is mounted).
  const { addOns, ensureLoaded } = useAddOns();
  useEffect(() => { ensureLoaded(); }, [ensureLoaded]);

  const placeholders = useMemo(
    () => visiblePlaceholders(keys, addOnPlaceholders(addOns)),
    [keys, addOns]
  );

  const handleClick = (event, key) => {
    event.preventDefault();
    // copyText: Clipboard-API mit execCommand-Fallback (ältere iOS-Safari, http-Dev-Hosts).
    copyText(key).then(ok => { if (ok) startSuccessBarSuccess('In Zwischenablage kopiert'); });
  };

  return (
    <div className={'placeholderTipps'}>
      {placeholders.map(({ key, label }) => (
        <Tooltip key={key} text={label}>
          <button type="button" onClick={(e) => handleClick(e, key)}>{key}</button>
        </Tooltip>
      ))}
    </div>
  );
}

export default TextblockEditorPlaceholder;
